<template>
  <HeaderSUUQ v-if="display.mobile.value" />
  <div class="header-fluid-container">
    <Header />
  </div>
  <v-container fluid class="pt-2 pb-4 mt-4">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="10">
        <!-- Page Header -->
        <div class="d-flex justify-space-between align-center mb-8">
          <div>
            <h1 class="text-h5 font-weight-bold">My Favorites</h1>
            <p class="subtitle-2">
              Create a list to organize your favorite listings
            </p>
          </div>
        </div>

        <!-- Sidebar and Content -->
        <v-row dense class="flex-column flex-md-row">
          <!-- Sidebar -->
          <v-col cols="12" md="3" class="mb-4 mb-md-0">
            <v-sheet elevation="1" class="rounded-lg overflow-hidden">
              <div class="d-flex justify-space-between align-center pa-4">
                <span class="subtitle-1 font-weight-medium"
                  >Favorite Lists</span
                >
                <v-btn class="create-list" text small @click="openCreateDialog"
                  >+ Create a List</v-btn
                >
              </div>
              <v-divider />

              <!-- Scrollable List -->
              <div class="sidebar-list">
                <div
                  v-for="list in favoriteLists"
                  :key="list.id === null ? 'all' : list.id"
                  class="list-card"
                  :class="{ 'active-card': list.id === selectedListId }"
                  @click="selectList(list.id)"
                >
                  <img :src="list.image_url || placeholder" class="thumb" />
                  <div class="info">
                    <div class="title">
                      {{ list.name }}
                      <span v-if="list.default" class="badge">Default</span>
                    </div>
                    <div class="count">{{ list.count }} Saved Ads</div>
                  </div>
                  <div v-if="list.id !== null" class="dropdown" @click.stop>
                    <button class="dots-btn" @click.stop="toggleMenu(list.id)">
                      ⋮
                    </button>
                    <ul
                      v-if="openMenu === list.id"
                      class="dropdown-menu"
                      @click.stop
                    >
                      <li @click="openEditDialog(list)">Edit</li>
                      <li @click="deleteList(list.id)">Delete</li>
                    </ul>
                  </div>
                </div>
              </div>

              <v-card
                v-if="!favoriteLists.length"
                outlined
                class="text-center pa-6 grey lighten-4"
              >
                <v-img
                  src="@/assets/empty-lists.svg"
                  height="80"
                  width="80"
                  class="mx-auto mb-4"
                />
                <div class="subtitle-1">No lists yet</div>
                <div class="caption mb-4">Create your personalized list</div>
                <v-btn class="create-list" color="primary" @click="openCreateDialog"
                  >Create a List</v-btn
                >
              </v-card>
            </v-sheet>
          </v-col>

          <!-- Main Content -->
          <v-col cols="12" md="9">
            <v-sheet elevation="1" class="rounded-lg pa-4">
              <div
                class="d-flex flex-wrap justify-space-between align-center mb-4"
              >
                <h2 class="subtitle-1 font-weight-medium">All Favorites</h2>
                <v-spacer />
                <v-text-field
                  v-model="searchQuery"
                  placeholder="Search..."
                  append-icon="mdi-magnify"
                  variant="outlined"
                  dense
                  small
                  hide-details
                  class="mr-2 mb-2"
                />
                <v-select
                  v-model="selectedCategory"
                  :items="categoryOptions"
                  placeholder="All Categories"
                  variant="outlined"
                  dense
                  hide-details
                  class="mb-2"
                  style="min-width: 160px"
                />
              </div>

              <v-divider />

              <v-row class="mt-4" dense>
                <v-col v-for="fav in filteredFavorites" :key="fav.id" cols="12">
                  <v-sheet
                    outlined
                    elevation="1"
                    class="favorite-item d-flex pa-4 flex-column flex-md-row"
                  >
                    <div class="carousel-wrapper">
                      <v-carousel hide-delimiter-background cycle height="200">
                        <v-carousel-item
                          v-for="img in fav.listing.files"
                          :key="img.id"
                        >
                          <v-img
                            :src="API_URL + img.url"
                            contain
                            height="200"
                          />
                        </v-carousel-item>
                      </v-carousel>

                      <v-btn
                        icon
                        class="overlay-btn favorite-btn"
                        @click="toggleFavorite(fav)"
                      >
                        <v-icon color="red">mdi-heart</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        class="overlay-btn share-btn"
                        @click.stop="openShare(fav.listing)"
                      >
                        <v-icon>mdi-share-variant</v-icon>
                      </v-btn>
                    </div>

                    <div class="details pl-md-6 mt-4 mt-md-0" style="flex: 1">
                      <h3 class="subtitle-1 font-weight-medium mb-1">
                        {{ fav.listing.title }}
                      </h3>
                      <div class="text-subtitle-2 font-weight-bold mb-1">
                        {{ fav.listing.currency }} {{ fav.listing.price }}
                      </div>
                      <div class="grey--text text-caption d-flex align-center">
                        <v-icon small class="mr-1">mdi-map-marker</v-icon>
                        {{ fav.listing.location?.location_name }},
                        {{ fav.listing.city?.country.name }} •
                        {{ formatDate(fav.created_at) }}
                      </div>
                      <div class="d-flex align-center mt-4">
                        <v-btn outlined small class="mr-2">
                          <v-icon left small>mdi-phone</v-icon>Call
                        </v-btn>
                        <v-btn outlined small>
                          <v-icon left small>mdi-whatsapp</v-icon>WhatsApp
                        </v-btn>
                      </div>
                    </div>
                  </v-sheet>
                </v-col>

                <v-col cols="12" v-if="!filteredFavorites.length">
                  <div class="text-center pa-8">
                    <v-img
                      src="@/assets/empty-state1.svg"
                      contain
                      height="300"
                    />
                    <div class="text-h6 mt-4">You have no favorites yet</div>
                    <div class="caption mb-4">
                      Use the favorite icon to save listings that you want to
                      check later
                    </div>
                    <v-btn
                      style="text-transform: none"
                      color="primary"
                      @click="showAddFromAllDialog = true"
                    >
                      Add From All Favorites
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-sheet>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Create List Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="400">
      <v-card>
        <v-card-title>Create a List</v-card-title>
        <v-card-text>
          <v-text-field variant="outlined" v-model="newListName" label="List Name" clearable />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showCreateDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="createList">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit List Dialog -->
    <v-dialog v-model="showEditDialog" max-width="400">
      <v-card>
        <v-card-title>Edit List</v-card-title>
        <v-card-text>
          <v-text-field v-model="editListName" label="List Name" clearable />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showEditDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="updateList">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showAddFromAllDialog" max-width="600">
      <v-card>
        <v-card-title class="headline"
          >Select ads from all favorites</v-card-title
        >
        <v-card-subtitle class="pb-0" v-if="allFavs.length > 0">
          <div class="d-flex justify-space-between">
            All Favorites
            <span class="badge">Default</span>
            <span class="grey--text text--darken-1 ml-auto">
              {{ allFavs.length }} Saved Ad{{ allFavs.length === 1 ? "" : "s" }}
            </span>
          </div>
        </v-card-subtitle>
        <v-divider />
        <v-card-text>
          <v-row dense>
            <v-col v-for="fav in allFavs" :key="fav.id" cols="12" sm="6" md="4">
              <v-card class="pa-0" outlined tile>
                <v-img :src="API_URL + fav.listing.files[0].url" height="120">
                  <template #placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-1"
                      />
                    </v-row>
                  </template>
                  <v-checkbox
                    v-model="selectedAds"
                    :value="fav.id"
                    color="primary"
                    class="checkbox--overlay"
                  />
                </v-img>

                <v-card-text class="pt-2 pb-1">
                  <div class="text-subtitle-2 font-weight-medium">
                    {{ fav.listing.currency }} {{ fav.listing.price }}
                  </div>
                  <div class="grey--text text-caption">
                    {{ fav.listing.title }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <div class="subtitle-2">
            {{ selectedAds.length }} ad{{ selectedAds.length === 1 ? "" : "s" }}
            selected
          </div>
          <v-spacer />
          <v-btn text @click="showAddFromAllDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedAds.length"
            @click="addSelectedToList"
          >
            Add To List
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Share Dialog -->
    <v-dialog v-model="shareDialog" max-width="800px">
      <v-card>
        <v-card-title class="d-flex align-center">
          Share this listing
          <v-spacer />
          <v-btn icon @click="shareDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row justify="space-around" class="mb-4">
            <v-btn
              icon
              :href="`https://wa.me/?text=${encodeURIComponent(
                currentShareUrl
              )}`"
              target="_blank"
            >
              <v-icon color="#25D366">mdi-whatsapp</v-icon>
            </v-btn>
            <v-btn
              icon
              :href="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                currentShareUrl
              )}`"
              target="_blank"
            >
              <v-icon color="#3b5998">mdi-facebook</v-icon>
            </v-btn>
            <v-btn
              icon
              :href="`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                currentShareUrl
              )}`"
              target="_blank"
            >
              <v-icon color="#1DA1F2">mdi-twitter</v-icon>
            </v-btn>
            <v-btn
              icon
              :href="`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                currentShareUrl
              )}`"
              target="_blank"
            >
              <v-icon color="#0077B5">mdi-linkedin</v-icon>
            </v-btn>
            <v-btn
              icon
              :href="`mailto:?subject=${encodeURIComponent(
                emailSubject
              )}&body=${encodeURIComponent(currentShareUrl)}`"
            >
              <v-icon color="#9c27b0">mdi-email</v-icon>
            </v-btn>
          </v-row>
          <v-text-field
            v-model="currentShareUrl"
            readonly
            hide-details
            append-inner-icon="mdi-content-copy"
            @click:append-inner="copyLink"
            label="Link"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="copyLink">Copy</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
  <FooterComponent v-if="!display.mobile.value" />
  <FooterMobile v-if="display.mobile.value" />
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { API_URL } from "@/helpers/api";
import Header from "./Header.vue";
import FooterComponent from "./footer/FooterComponent.vue";
import FavoriteService from "@/services/favorite.service";
import FavoriteListService from "@/services/favoriteList.service";
import { useDisplay } from "vuetify";
import FooterMobile from "./footer/FooterMobile.vue";
import HeaderSUUQ from "./HeaderSUUQ.vue";

const store = useStore();
const placeholder = "https://via.placeholder.com/50";

const display = useDisplay();

// Dialog state
const showCreateDialog = ref(false);
const newListName = ref("");
const showEditDialog = ref(false);
const editListName = ref("");
const editingListId = ref(null);
const openMenu = ref(null);
const shareDialog = ref(false);
const currentShareUrl = ref("");
// Search & Sort
const searchQuery = ref("");
const sortMenu = ref(false);
const sortOption = ref("date_desc");
const sortOptions = [
  { text: "Default", value: "default" },
  { text: "Price Lowest to Highest", value: "price_asc" },
  { text: "Price Highest to Lowest", value: "price_desc" },
  { text: "Oldest to Newest", value: "date_asc" },
  { text: "Newest to Oldest", value: "date_desc" },
  { text: "Favorite Oldest to Newest", value: "fav_date_asc" },
  { text: "Favorite Newest to Oldest", value: "fav_date_desc" },
];
const sortOptionLabel = computed(() => {
  const opt = sortOptions.find((o) => o.value === sortOption.value);
  return opt ? opt.text : "Default";
});
function setSort(val) {
  sortOption.value = val;
  sortMenu.value = false;
}

const lists = computed(() => store.state.favoriteList.lists);

console.log("Some lists", lists.value);

// — your two caches now come from getters:
const allFavs = computed(() => store.getters["favorite/allFavorites"]);
//const filteredFavorites = computed(() => store.getters["favorite/favorites"]);
const selectedListId = computed(() => store.getters["favoriteList/activeId"]);

console.log("All favorites", allFavs.value);

const emailSubject = ref(typeof document !== "undefined" ? document.title : "");

// dialog & selection state
const showAddFromAllDialog = ref(false);
const selectedAds = ref([]);

function toggleMenu(id) {
  openMenu.value = openMenu.value === id ? null : id;
}

const favoriteLists = computed(() => {
  // 1️⃣ build your “real” lists, one entry per favorite‐list
  const realLists = lists.value.map((list) => {
    // only the favs that actually belong to this list via the pivot:
    const favsInList = allFavs.value.filter((fav) =>
      fav.lists.some((l2) => l2.id === list.id)
    );

    return {
      ...list,
      default: false,
      count: favsInList.length,
      image_url: favsInList[0]?.listing.files?.[0]?.url
        ? API_URL + favsInList[0].listing.files[0].url
        : placeholder,
    };
  });

  // 2️⃣ if there are *no* real lists but *some* favs, show just “All Favorites”
  if (!realLists.length && allFavs.value.length) {
    return [
      {
        id: null,
        name: "All Favorites",
        default: true,
        count: allFavs.value.length,
        image_url: allFavs.value[0]?.listing.files?.[0]?.url
          ? API_URL + allFavs.value[0].listing.files[0].url
          : placeholder,
      },
    ];
  }

  // 3️⃣ if there *are* real lists, prepend the pseudo “All Favorites”
  if (realLists.length) {
    return [
      {
        id: null,
        name: "All Favorites",
        default: true,
        count: allFavs.value.length,
        image_url: allFavs.value[0]?.listing.files?.[0]?.url
          ? API_URL + allFavs.value[0].listing.files[0].url
          : placeholder,
      },
      ...realLists,
    ];
  }

  // 4️⃣ otherwise (no lists AND no favs) → empty so your “no lists yet” UI shows
  return [];
});

async function reloadFavorites() {
  await store.dispatch("favorite/fetchFavorites", selectedListId.value);
}

// name of the active list
const currentListName = computed(() => {
  const l = store.getters["favoriteList/activeList"];
  return l?.name || "Favorites";
});

// the favorites in the active list, then filtered & sorted
// const filteredFavorites = computed(() => {
//   let items = store.getters["favorite/favorites"];

//   console.log("I want to see these items", items);

//   if (searchQuery.value) {
//     items = items.filter((f) =>
//       f.listing.title.toLowerCase().includes(searchQuery.value.toLowerCase())
//     );
//   }

//   items.sort((a, b) => {
//     const ad = new Date(a.created_at),
//       bd = new Date(b.created_at);
//     if (sortOption.value === "date_desc") return bd - ad;
//     if (sortOption.value === "date_asc") return ad - bd;
//     // extend here for price_asc/desc if desired
//     return 0;
//   });

//   return items;
// });

const filteredFavorites = computed(() => {
  console.log("Items in all favs", allFavs.value);
  let items = allFavs.value;

  console.log("Items in all favs", items);

  // if we’re on a real list (not “All”), filter it down
  if (selectedListId.value !== null) {
    items = items.filter((f) =>
      f.lists?.some((l) => l.id === selectedListId.value)
    );
  }

  if (searchQuery.value) {
    items = items.filter((f) =>
      f.listing.title.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  items.sort((a, b) => {
    const ad = new Date(a.created_at),
      bd = new Date(b.created_at);
    if (sortOption.value === "date_desc") return bd - ad;
    if (sortOption.value === "date_asc") return ad - bd;
    return 0;
  });

  return items;
});

// === Lifecycle ===

// onMounted(async () => {
//   // master cache
//   await store.dispatch("favorite/fetchAllFavorites");

//   // sidebar lists (and pick default activeId)
//   await store.dispatch("favoriteList/fetchLists");

//   // load whatever list is active (could be `null` = “All”)
//   const active = store.getters["favoriteList/activeId"];
//   await store.dispatch("favorite/fetchFavorites", active);
// });

onMounted(async () => {
  await store.dispatch("favorite/fetchAllFavorites");
  await store.dispatch("favoriteList/fetchLists");
  await store.dispatch("favorite/fetchFavorites", selectedListId.value);
});

// watch(
//   () => store.getters["favoriteList/activeId"],
//   (newListId) => {
//     store.dispatch("favorite/fetchFavorites", newListId);
//   }
// );

watch(
  () => selectedListId.value,
  (id) => store.dispatch("favorite/fetchFavorites", id)
);

async function selectList(id) {
  // 1) mark that list active in the sidebar
  await store.dispatch("favoriteList/setActive", id);

  // 2) then fetch its favorites into filteredFavorites
  await store.dispatch("favorite/fetchFavorites", id);
}

// open/close dialogs
const openCreateDialog = () => (showCreateDialog.value = true);
const openEditDialog = (list) => {
  editingListId.value = list.id;
  editListName.value = list.name;
  showEditDialog.value = true;
};

// create a new list
async function createList() {
  if (!newListName.value.trim()) return;

  // delegate everything to the Vuex action
  await store.dispatch("favoriteList/createList", newListName.value);
  newListName.value = "";
  showCreateDialog.value = false;
}

// update an existing list
async function updateList() {
  if (!editListName.value.trim()) return;
  await store.dispatch("favoriteList/updateList", {
    id: editingListId.value,
    name: editListName.value,
  });
  showEditDialog.value = false;
}

// delete a list
async function deleteList(id) {
  if (!confirm("Delete this list?")) return;
  await store.dispatch("favoriteList/deleteList", id);
}

// remove a single favorite
async function toggleFavorite(fav) {
  await store.dispatch("favorite/removeFavorite", fav.id);
}

// date formatter
const formatDate = (date) => new Date(date).toLocaleDateString();

// when they click “Add To List”

async function addFromAllFavorites() {
  if (!selectedListId.value) return;
  // pick every favorite that isn’t already in a list
  const toMove = allFavs.value.filter((f) => f.favorite_list_id === null);
  for (const fav of toMove) {
    await FavoriteService.updateFavorite(fav.id, {
      favorite_list_id: selectedListId.value,
    });
  }
  // refresh UI
  await reloadFavorites();
}

async function addSelectedToList() {
  const listId = store.getters["favoriteList/activeId"];
  if (!listId) return;

  // call the pivot endpoint for each checked favorite
  await Promise.all(
    selectedAds.value.map((favId) => {
      const fav = store.getters["favorite/allFavorites"].find(
        (f) => f.id === favId
      );
      return FavoriteListService.addListingToList(listId, fav.listing.id);
    })
  );

  // clear & close dialog
  selectedAds.value = [];
  showAddFromAllDialog.value = false;

  // refresh everything:
  //  • re‐load master cache
  await store.dispatch("favorite/fetchAllFavorites");
  //  • re‐load sidebar thumbnails
  await store.dispatch("favoriteList/fetchLists");
  //  • re‐load the active list’s items
  await store.dispatch("favorite/fetchFavorites", listId);
}

function openShare(listing) {
  // build whatever URL your site uses; here I’m assuming slug or fallback to ID
  currentShareUrl.value = `${window.location.origin}/listings/${
    listing.id || listing.slug
  }`;
  shareDialog.value = true;
}

// copy to clipboard, with a tiny confirmation (you can swap in a snackbar)
async function copyLink() {
  try {
    await navigator.clipboard.writeText(currentShareUrl.value);
    // console.log('Copied!', currentShareUrl.value)
  } catch (e) {
    console.error("Copy failed", e);
  }
}
</script>

<style scoped>
.sidebar-list {
  padding: 8px;
  max-height: 500px;
  overflow-y: auto;
}

.list-card {
  position: relative;
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
  border: 1px solid #eee;
  border-radius: 8px;
  cursor: pointer;
}

.list-card.active-card {
  border-left: 4px solid #d32f2f;
}

.thumb {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 12px;
}

.info .title {
  font-weight: 500;
  margin-bottom: 4px;
}
.info .count {
  color: #666;
  font-size: 0.875rem;
}
.badge {
  background: #1976d2;
  color: white;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.75rem;
  margin-left: 8px;
}

/* Dropdown */
.dropdown {
  position: absolute;
  top: 8px;
  right: 8px;
}
.dots-btn {
  background: none;
  border: none;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
}
.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  list-style: none;
  padding: 4px 0;
  min-width: 120px;
  z-index: 100;
}
.dropdown-menu li {
  padding: 8px 12px;
  cursor: pointer;
}
.dropdown-menu li:hover {
  background-color: #f5f5f5;
}

/* Empty state */
.empty-state-card {
  text-align: center;
  padding: 24px;
  border: 1px dashed #ccc;
  border-radius: 8px;
  background: #fafafa;
}
.empty-img {
  margin-bottom: 16px;
}
.empty-title {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 8px;
}
.empty-caption {
  color: #666;
  margin-bottom: 16px;
}
.empty-btn {
  background: #1976d2;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}
.header-title {
  text-align: center;
  margin-bottom: 24px;
}
.header-title h1 {
  margin: 0;
  font-size: 24px;
}
.header-title p {
  margin: 8px 0 0;
  color: #6c757d;
  font-size: 14px;
}
.favorite-lists-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.favorite-lists-header h2 {
  margin: 0;
  font-size: 18px;
}
.favorite-lists-header button {
  background: none;
  border: none;
  color: #007bff;
  font-size: 16px;
  cursor: pointer;
}
.list-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 16px;
}
.list-item img {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 12px;
}
.list-item .info {
  display: flex;
  flex-direction: column;
}
.list-item .info .title {
  margin: 0;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.list-item .info .title .badge {
  background-color: #0d6efd;
  color: #fff;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 12px;
}
.list-item .info .count {
  margin-top: 4px;
  color: #6c757d;
  font-size: 14px;
}
.empty-state {
  flex: 1;
  border: 1px dashed #dee2e6;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 24px;
  text-align: center;
  color: #6c757d;
}
.empty-state .icon {
  font-size: 32px;
  margin-bottom: 16px;
}
.empty-state h3 {
  margin: 0;
  font-size: 18px;
}
.empty-state p {
  margin: 8px 0 16px;
  font-size: 14px;
}
.empty-state button {
  padding: 8px 16px;
  border: 1px solid #007bff;
  background: #fff;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
}
.content {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
}
.content-header h2 {
  margin: 0;
  font-size: 18px;
  flex: 0 0 auto;
}
.content-header .search {
  flex: 1;
}
.content-header input[type="text"] {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}
.content-header select {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  cursor: pointer;
}
.card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-top: 16px;
}
.card .card-image {
  position: relative;
  width: 100%;
  padding-top: 60%;
  background-size: cover;
  background-position: center;
}
.card .card-icons {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
}
.card .card-icons .icon {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: #dc3545;
}
.card .card-body {
  padding: 16px;
  box-sizing: border-box;
}
.card-body h3 {
  margin: 0;
  font-size: 16px;
}
.card-body p {
  margin: 8px 0;
  color: #6c757d;
  font-size: 14px;
}
.price {
  font-size: 18px;
  font-weight: bold;
  margin: 8px 0;
}
.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0;
}
.tags span {
  background: #f1f3f5;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}
.actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}
.actions button {
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.actions .call {
  border: 1px solid #dc3545;
  background: #fff;
  color: #dc3545;
}

.actions .whatsapp {
  background: #28a745;
  color: #fff;
}

.border-left-primary {
  border-left: 4px solid #d32f2f; /* or your primary color */
}

.list-card {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  cursor: pointer;
}
.active-card {
  border-left: 4px solid #d32f2f; /* your “active” color */
}
.action-menu {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
}

.default {
  color: white;
  background-color: #4169e1;
  border-radius: 5px;
  font-size: 12px;
}
.menu-activator {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.carousel-wrapper {
  position: relative;
  flex-shrink: 0;
  width: 300px; /* or whatever width you want */
}

.overlay-btn {
  position: absolute;
  top: 8px;
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  font-size: 10px;
}

.favorite-btn {
  right: 50px;
}

.share-btn {
  right: 100px;
  font-size: 10px;
}

.favorite-item {
  display: flex;
  align-items: flex-start;
}

.empty-img {
  max-height: 300px;
  width: auto;
}

.checkbox--overlay {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  padding: 2px;
  z-index: 10;
}

@media (max-width: 768px) {
  .list-card {
    flex-direction: column;
    align-items: flex-start;
  }
  .thumb {
    width: 100%;
    height: auto;
    margin-right: 0;
    margin-bottom: 8px;
  }
  .details {
    padding-left: 0 !important;
  }
  .carousel-wrapper {
    width: 100%;
  }
  .favorite-item {
    flex-direction: column;
  }
  .overlay-btn.favorite-btn {
    right: 16px;
  }
  .overlay-btn.share-btn {
    right: 60px;
  }
  .v-dialog {
    width: 100% !important;
    max-width: 95% !important;
    margin: auto;
  }
}

.header-fluid-container {
  max-width: 1400px !important;
  width: 100% !important;
  margin: 0 auto !important;
  padding-left: 16px !important;
  padding-right: 16px !important;

  /* Optional: add responsive padding for mobile */
}

.create-list{
  text-transform: none !important;
}
</style>
